// Granular Refresh spec §3. The canonical manifest of fields a row snapshots
// from its Campaign — the SINGLE source of truth shared by:
//
//   - the bulk Refresh-from-Campaign modal (which `refreshable: true`
//     entries to show, grouped by section, with labels + previews)
//   - getDriftedFields / the modification badges (per-field drift)
//   - the server /refresh-fields endpoint (validate fieldKeys + apply a
//     partial snapshot via each entry's `storage` descriptor)
//
// Deliberately Vite-free (no import.meta) so the server imports it directly,
// the same pattern as src/lib/allowedEmails.ts. Pure data + pure predicates;
// NO React, NO Firestore.
//
// ── Parity-with-diffCampaignVsRow contract ──────────────────────────────
// Each entry's `isModified` reproduces, key-for-key, the comparison logic
// that previously lived inline in src/modules/rollouts/diffCampaignVsRow.ts.
// The Granular Refresh slice (commit 9) makes diffCampaignVsRow delegate
// here so there is exactly ONE comparison implementation. The manifest
// therefore covers EVERY key diffCampaignVsRow emitted (incl. the two
// deprecated Overview URLs, kept as `refreshable: false` so badge behaviour
// is unchanged) PLUS `metaCreativeFolderName`, which was snapshotted +
// spec-listed but missing from the old diff — adding it is an intended
// behaviour change (the badge now correctly flags creative-folder drift).
//
// Excluded entirely (no Campaign source — spec §3.2): per-row budgets,
// dates, AM, ready flags, execution booleans, notes, localAssetsUrl,
// localAssetLabels. Budget is NOT in the snapshot pattern at all (Campaign
// has no budget fields), so the spec §3.1 budget bullet does not apply.

import type { AccountCampaign } from '../types/accountCampaign.js';
import type { Campaign, CampaignLeadForm } from '../types/campaign.js';
import { fieldsEqual, normArray } from './fieldsEqual.js';

export type RefreshSection = 'meta' | 'google' | 'assets' | 'landing' | 'naming';

export type RefreshableFieldKey =
  // Meta content
  | 'metaAdCopy.metaAdType'
  | 'metaAdCopy.bodyCopy'
  | 'metaAdCopy.headlines'
  | 'metaAdCopy.descriptions'
  | 'metaAdCopy.ctaButton'
  | 'leadForm'
  // Google content
  | 'googleAssets.headlines'
  | 'googleAssets.descriptions'
  | 'googleAssets.path1'
  | 'googleAssets.path2'
  // Assets
  | 'metaAssetsFolderUrl'
  | 'metaAssetLabels'
  | 'metaCreativeFolderName'
  | 'googleAssetsFolderUrl'
  | 'otherAssetsFolderUrl'
  // Landing pages
  | 'landingPageType'
  | 'customLandingPageUrl'
  | 'metaLandingPageOverride'
  | 'googleLandingPageOverride'
  | 'utmConfig'
  // Naming & metadata
  | 'campaignDescriptor'
  | 'metaCampaignNameTemplate'
  | 'googleAdLabelTemplate'
  // Deprecated Overview URLs — comparison-only (kept for badge parity),
  // never shown in the modal (refreshable: false). Plan-owned post the
  // consolidation; always null on modern Campaigns.
  | 'offerMechanicsUrl'
  | 'kyckCampaignOverviewUrl';

// How the server's /refresh-fields endpoint writes this field when the AM
// selects it. The server computes the full Campaign snapshot once, then:
//   - 'top'      → copy snapshot[field] straight onto the patch
//   - 'nested'   → splice snapshot[parent][child] into the row's CURRENT
//                  parent object (other sub-fields preserved). Selected
//                  sibling sub-fields are merged into one parent write.
//   - 'leadForm' → copy snapshot.leadForm AND snapshot.leadFormId together
//                  (the pair is snapshotted atomically).
export type RefreshStorage =
  | { kind: 'top'; field: keyof AccountCampaign }
  | { kind: 'nested'; parent: 'metaAdCopy' | 'googleAssets'; child: string }
  | { kind: 'leadForm' };

export type RefreshableFieldEntry = {
  key: RefreshableFieldKey;
  section: RefreshSection;
  label: string;
  // false → comparison-only (badge parity), never offered in the modal.
  refreshable: boolean;
  storage: RefreshStorage;
  // Per-field drift: row value ≠ Campaign's current value. Reproduces
  // diffCampaignVsRow's per-key semantics exactly (see file header).
  isModified: (row: AccountCampaign, campaign: Campaign) => boolean;
  // Count for array-valued fields → "Headlines (5)". undefined for scalars.
  count?: (campaign: Campaign) => number;
  // Plain-text rendering of the Campaign's CURRENT value. Truncation /
  // expand is the modal component's job (spec §2.3) — this returns the
  // full string; '(empty)' for null / '' / empty arrays.
  preview: (campaign: Campaign) => string;
  // Per-element strings for the expanded ("Show all N →") view, defined
  // only on array-valued fields (the same set that carry `count`). The
  // collapsed row still uses the joined `preview`.
  items?: (campaign: Campaign) => string[];
};

// ── shared sub-predicates (replicate diffCampaignVsRow fan-out) ──────────

// metaAdCopy / googleAssets are snapshotted as whole objects but compared
// per sub-field. Old diff behaviour: both parents present → compare the
// sub-field; exactly one parent null → ALL sub-fields drift (so the refresh
// count matches the number of populated editors); both null → no drift.
function subModified<P extends 'metaAdCopy' | 'googleAssets'>(
  parent: P,
  pick: (o: NonNullable<AccountCampaign[P]> | NonNullable<Campaign[P]>) => unknown,
  rowParent: AccountCampaign[P],
  campaignParent: Campaign[P],
): boolean {
  if (rowParent && campaignParent) {
    return !fieldsEqual(pick(rowParent), pick(campaignParent));
  }
  if (rowParent == null && campaignParent == null) return false;
  return true; // exactly one null → fan-out drift
}

// Inherit-on-undefined (Pre-P2b-3 round 2 Q4): a row whose snapshot field
// is `undefined` (legacy / never-written) inherits the Campaign — no drift.
// Once it carries a value (incl. explicit null), strict compare vs the
// Campaign's value coalesced to null.
function inheritScalarModified(
  rowVal: string | null | undefined,
  campaignVal: string | null | undefined,
): boolean {
  if (rowVal === undefined) return false;
  return rowVal !== (campaignVal ?? null);
}

// Multi Lead Form spec §1.2: compare the row's snapshot against the
// Campaign's CURRENT default entry (defaultLeadFormId → entry, else null).
function campaignDefaultLeadForm(campaign: Campaign): CampaignLeadForm | null {
  return (
    (campaign.campaignLeadForms ?? []).find(
      (e) => e.id === (campaign.defaultLeadFormId ?? null),
    ) ?? null
  );
}

// ── preview helpers ─────────────────────────────────────────────────────

const EMPTY = '(empty)';

function previewScalar(v: string | null | undefined): string {
  if (v == null || v === '') return EMPTY;
  return v;
}

function previewStringArray(v: readonly string[] | null | undefined): string {
  const a = normArray(v as string[] | null | undefined);
  return a.length === 0 ? EMPTY : a.join(' · ');
}

function previewObject(v: unknown): string {
  if (v == null) return EMPTY;
  return JSON.stringify(v);
}

// ── the manifest ────────────────────────────────────────────────────────

export const REFRESHABLE_FIELDS: readonly RefreshableFieldEntry[] = [
  // ── META CONTENT ──
  {
    key: 'metaAdCopy.bodyCopy',
    section: 'meta',
    label: 'Body copy',
    refreshable: true,
    storage: { kind: 'nested', parent: 'metaAdCopy', child: 'bodyCopy' },
    isModified: (r, c) =>
      subModified('metaAdCopy', (o) => normArray(o.bodyCopy), r.metaAdCopy, c.metaAdCopy),
    count: (c) => normArray(c.metaAdCopy?.bodyCopy).length,
    preview: (c) => previewStringArray(c.metaAdCopy?.bodyCopy),
    items: (c) => normArray(c.metaAdCopy?.bodyCopy),
  },
  {
    key: 'metaAdCopy.headlines',
    section: 'meta',
    label: 'Headlines',
    refreshable: true,
    storage: { kind: 'nested', parent: 'metaAdCopy', child: 'headlines' },
    isModified: (r, c) =>
      subModified('metaAdCopy', (o) => normArray(o.headlines), r.metaAdCopy, c.metaAdCopy),
    count: (c) => normArray(c.metaAdCopy?.headlines).length,
    preview: (c) => previewStringArray(c.metaAdCopy?.headlines),
    items: (c) => normArray(c.metaAdCopy?.headlines),
  },
  {
    key: 'metaAdCopy.descriptions',
    section: 'meta',
    label: 'Descriptions',
    refreshable: true,
    storage: { kind: 'nested', parent: 'metaAdCopy', child: 'descriptions' },
    isModified: (r, c) =>
      subModified(
        'metaAdCopy',
        (o) => normArray(o.descriptions),
        r.metaAdCopy,
        c.metaAdCopy,
      ),
    count: (c) => normArray(c.metaAdCopy?.descriptions).length,
    preview: (c) => previewStringArray(c.metaAdCopy?.descriptions),
    items: (c) => normArray(c.metaAdCopy?.descriptions),
  },
  {
    key: 'metaAdCopy.ctaButton',
    section: 'meta',
    label: 'CTA',
    refreshable: true,
    storage: { kind: 'nested', parent: 'metaAdCopy', child: 'ctaButton' },
    isModified: (r, c) =>
      subModified('metaAdCopy', (o) => o.ctaButton, r.metaAdCopy, c.metaAdCopy),
    preview: (c) => previewScalar(c.metaAdCopy?.ctaButton ?? null),
  },
  {
    key: 'metaAdCopy.metaAdType',
    section: 'meta',
    label: 'Meta ad type',
    refreshable: true,
    storage: { kind: 'nested', parent: 'metaAdCopy', child: 'metaAdType' },
    isModified: (r, c) =>
      subModified('metaAdCopy', (o) => o.metaAdType, r.metaAdCopy, c.metaAdCopy),
    preview: (c) => previewScalar(c.metaAdCopy?.metaAdType ?? null),
  },
  {
    key: 'leadForm',
    section: 'meta',
    label: 'Lead form',
    refreshable: true,
    storage: { kind: 'leadForm' },
    isModified: (r, c) => !fieldsEqual(r.leadForm, campaignDefaultLeadForm(c)),
    preview: (c) => {
      const lf = campaignDefaultLeadForm(c);
      if (!lf) return EMPTY;
      return `${lf.name} — ${lf.formType}`;
    },
  },

  // ── GOOGLE CONTENT ──
  {
    key: 'googleAssets.headlines',
    section: 'google',
    label: 'Headlines',
    refreshable: true,
    storage: { kind: 'nested', parent: 'googleAssets', child: 'headlines' },
    isModified: (r, c) =>
      subModified(
        'googleAssets',
        (o) => normArray(o.headlines),
        r.googleAssets,
        c.googleAssets,
      ),
    count: (c) => normArray(c.googleAssets?.headlines).length,
    preview: (c) =>
      previewStringArray(normArray(c.googleAssets?.headlines).map((h) => h.text)),
    items: (c) => normArray(c.googleAssets?.headlines).map((h) => h.text),
  },
  {
    key: 'googleAssets.descriptions',
    section: 'google',
    label: 'Descriptions',
    refreshable: true,
    storage: { kind: 'nested', parent: 'googleAssets', child: 'descriptions' },
    isModified: (r, c) =>
      subModified(
        'googleAssets',
        (o) => normArray(o.descriptions),
        r.googleAssets,
        c.googleAssets,
      ),
    count: (c) => normArray(c.googleAssets?.descriptions).length,
    preview: (c) => previewStringArray(c.googleAssets?.descriptions),
    items: (c) => normArray(c.googleAssets?.descriptions),
  },
  {
    key: 'googleAssets.path1',
    section: 'google',
    label: 'Path 1',
    refreshable: true,
    storage: { kind: 'nested', parent: 'googleAssets', child: 'path1' },
    isModified: (r, c) =>
      subModified('googleAssets', (o) => o.path1, r.googleAssets, c.googleAssets),
    preview: (c) => previewScalar(c.googleAssets?.path1 ?? null),
  },
  {
    key: 'googleAssets.path2',
    section: 'google',
    label: 'Path 2',
    refreshable: true,
    storage: { kind: 'nested', parent: 'googleAssets', child: 'path2' },
    isModified: (r, c) =>
      subModified('googleAssets', (o) => o.path2 ?? null, r.googleAssets, c.googleAssets),
    preview: (c) => previewScalar(c.googleAssets?.path2 ?? null),
  },

  // ── ASSETS ──
  {
    key: 'metaAssetsFolderUrl',
    section: 'assets',
    label: 'Meta Assets folder URL',
    refreshable: true,
    storage: { kind: 'top', field: 'metaAssetsFolderUrl' },
    isModified: (r, c) => r.metaAssetsFolderUrl !== c.metaAssetsFolderUrl,
    preview: (c) => previewScalar(c.metaAssetsFolderUrl),
  },
  {
    key: 'metaAssetLabels',
    section: 'assets',
    label: 'Meta Asset labels',
    refreshable: true,
    storage: { kind: 'top', field: 'metaAssetLabels' },
    isModified: (r, c) =>
      !fieldsEqual(normArray(r.metaAssetLabels), normArray(c.metaAssetLabels)),
    count: (c) => normArray(c.metaAssetLabels).length,
    preview: (c) =>
      previewStringArray(normArray(c.metaAssetLabels).map((l) => l.label)),
    items: (c) => normArray(c.metaAssetLabels).map((l) => l.label),
  },
  {
    key: 'metaCreativeFolderName',
    section: 'assets',
    label: 'Meta Creative Folder Name',
    refreshable: true,
    storage: { kind: 'top', field: 'metaCreativeFolderName' },
    // NEW vs the old diff (intended behaviour change — see file header).
    // string|null on both sides; coalesce for legacy-undefined safety.
    isModified: (r, c) =>
      (r.metaCreativeFolderName ?? null) !== (c.metaCreativeFolderName ?? null),
    preview: (c) => previewScalar(c.metaCreativeFolderName),
  },
  {
    key: 'googleAssetsFolderUrl',
    section: 'assets',
    label: 'Google Assets folder URL',
    refreshable: true,
    storage: { kind: 'top', field: 'googleAssetsFolderUrl' },
    isModified: (r, c) => r.googleAssetsFolderUrl !== c.googleAssetsFolderUrl,
    preview: (c) => previewScalar(c.googleAssetsFolderUrl),
  },
  {
    key: 'otherAssetsFolderUrl',
    section: 'assets',
    label: 'Other Assets folder URL',
    refreshable: true,
    storage: { kind: 'top', field: 'otherAssetsFolderUrl' },
    isModified: (r, c) => r.otherAssetsFolderUrl !== c.otherAssetsFolderUrl,
    preview: (c) => previewScalar(c.otherAssetsFolderUrl),
  },

  // ── LANDING PAGES ──
  {
    key: 'landingPageType',
    section: 'landing',
    label: 'Landing page type',
    refreshable: true,
    storage: { kind: 'top', field: 'landingPageType' },
    isModified: (r, c) => r.landingPageType !== c.landingPageType,
    preview: (c) => previewScalar(c.landingPageType),
  },
  {
    key: 'customLandingPageUrl',
    section: 'landing',
    label: 'Custom landing URL',
    refreshable: true,
    storage: { kind: 'top', field: 'customLandingPageUrl' },
    isModified: (r, c) => r.customLandingPageUrl !== c.customLandingPageUrl,
    preview: (c) => previewScalar(c.customLandingPageUrl),
  },
  {
    key: 'metaLandingPageOverride',
    section: 'landing',
    label: 'Meta landing override',
    refreshable: true,
    storage: { kind: 'top', field: 'metaLandingPageOverride' },
    isModified: (r, c) =>
      !fieldsEqual(r.metaLandingPageOverride, c.metaLandingPageOverride),
    preview: (c) => previewObject(c.metaLandingPageOverride),
  },
  {
    key: 'googleLandingPageOverride',
    section: 'landing',
    label: 'Google landing override',
    refreshable: true,
    storage: { kind: 'top', field: 'googleLandingPageOverride' },
    isModified: (r, c) =>
      !fieldsEqual(r.googleLandingPageOverride, c.googleLandingPageOverride),
    preview: (c) => previewObject(c.googleLandingPageOverride),
  },
  {
    key: 'utmConfig',
    section: 'landing',
    label: 'UTM tags',
    refreshable: true,
    storage: { kind: 'top', field: 'utmConfig' },
    isModified: (r, c) => !fieldsEqual(r.utmConfig, c.utmConfig),
    preview: (c) => previewObject(c.utmConfig),
  },

  // ── NAMING & METADATA ──
  {
    key: 'campaignDescriptor',
    section: 'naming',
    label: 'Campaign descriptor',
    refreshable: true,
    storage: { kind: 'top', field: 'campaignDescriptor' },
    isModified: (r, c) =>
      inheritScalarModified(r.campaignDescriptor, c.campaignDescriptor),
    preview: (c) => previewScalar(c.campaignDescriptor),
  },
  {
    key: 'metaCampaignNameTemplate',
    section: 'naming',
    label: 'Meta naming template',
    refreshable: true,
    storage: { kind: 'top', field: 'metaCampaignNameTemplate' },
    isModified: (r, c) =>
      inheritScalarModified(r.metaCampaignNameTemplate, c.metaCampaignNameTemplate),
    preview: (c) => previewScalar(c.metaCampaignNameTemplate),
  },
  {
    key: 'googleAdLabelTemplate',
    section: 'naming',
    label: 'Google ad label template',
    refreshable: true,
    storage: { kind: 'top', field: 'googleAdLabelTemplate' },
    isModified: (r, c) =>
      inheritScalarModified(r.googleAdLabelTemplate, c.googleAdLabelTemplate),
    preview: (c) => previewScalar(c.googleAdLabelTemplate),
  },

  // ── COMPARISON-ONLY (badge parity; never in the modal) ──
  {
    key: 'offerMechanicsUrl',
    section: 'naming',
    label: 'Offer Mechanics URL',
    refreshable: false,
    storage: { kind: 'top', field: 'offerMechanicsUrl' },
    isModified: (r, c) => r.offerMechanicsUrl !== c.offerMechanicsUrl,
    preview: (c) => previewScalar(c.offerMechanicsUrl),
  },
  {
    key: 'kyckCampaignOverviewUrl',
    section: 'naming',
    label: 'Kyck Campaign Overview URL',
    refreshable: false,
    storage: { kind: 'top', field: 'kyckCampaignOverviewUrl' },
    isModified: (r, c) => r.kyckCampaignOverviewUrl !== c.kyckCampaignOverviewUrl,
    preview: (c) => previewScalar(c.kyckCampaignOverviewUrl),
  },
];

// Fast lookups -----------------------------------------------------------

export const REFRESHABLE_FIELD_BY_KEY: ReadonlyMap<
  RefreshableFieldKey,
  RefreshableFieldEntry
> = new Map(REFRESHABLE_FIELDS.map((e) => [e.key, e]));

// Modal-facing subset: entries the AM can actually select, in declaration
// order (which is also section order).
export const MODAL_REFRESHABLE_FIELDS: readonly RefreshableFieldEntry[] =
  REFRESHABLE_FIELDS.filter((e) => e.refreshable);

// Section display order + headings for the modal (spec §2.2).
export const REFRESH_SECTION_ORDER: readonly RefreshSection[] = [
  'meta',
  'google',
  'assets',
  'landing',
  'naming',
];

export const REFRESH_SECTION_LABEL: Record<RefreshSection, string> = {
  meta: 'Meta content',
  google: 'Google content',
  assets: 'Assets',
  landing: 'Landing pages',
  naming: 'Naming & metadata',
};

// Modal-facing entries grouped by section, sections in display order,
// empty sections omitted. Used by the modal to render <Section> blocks.
export function refreshableFieldsBySection(): Array<{
  section: RefreshSection;
  label: string;
  fields: RefreshableFieldEntry[];
}> {
  return REFRESH_SECTION_ORDER.map((section) => ({
    section,
    label: REFRESH_SECTION_LABEL[section],
    fields: MODAL_REFRESHABLE_FIELDS.filter((e) => e.section === section),
  })).filter((g) => g.fields.length > 0);
}

// Server-side fieldKey validation (spec §4.1). Only `refreshable: true`
// keys are accepted from the wire — the two comparison-only Overview URLs
// are not user-selectable.
export function isRefreshableFieldKey(k: string): k is RefreshableFieldKey {
  const e = REFRESHABLE_FIELD_BY_KEY.get(k as RefreshableFieldKey);
  return e !== undefined && e.refreshable;
}
